import React from 'react';
import xlsx from 'xlsx';
import { CancelToken } from 'axios';
import { Button } from 'antd';
import useAsyncApiFetch from 'src/hooks/useAsyncApiFetch';
import { ExportModal } from './ExportModal';

export const fileFormat = {
  csv: '.csv',
  tsv: '.tsv',
  xls: '.xls',
  xlsx: '.xlsx',
} as const;

export type FileFormat = keyof typeof fileFormat;

const getOptions = (format: FileFormat) => {
  if (format === 'tsv') {
    return { bookType: 'csv', FS: '\t' } as const;
  }
  return { bookType: format };
};

const addExtension = (fileName: string, format: FileFormat): string => {
  const ext = `.${format}`.toLowerCase();
  const name = fileName.trim();
  return name.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
};

export type ExportButtonProps<T extends object> = {
  fetcher: (token: CancelToken) => Promise<T>;
  format: FileFormat;
  extractor: (data: T) => T;
  fileName: string;
  worksheetName: string;
  mapping: (item: T) => T;
  disabled?: boolean;
  onStart?: () => void;
  isCategory?: boolean;
};

export const ExportButton = <T extends object>({
  fetcher,
  format,
  fileName,
  worksheetName,
  extractor,
  mapping,
  disabled,
  onStart,
}: ExportButtonProps<T>) => {
  const [{ loading, data }, startExport, cancelExport] = useAsyncApiFetch(fetcher, {
    dispatchCancelledError: true,
  });

  const handleClick = () => {
    if (onStart) {
      onStart();
    }
    startExport();
  };

  const handleComplete = () => {
    if (data) {
      const extractedData = extractor(data);
      const rows = Array.isArray(extractedData) ? extractedData : [extractedData];
      if (rows.length > 0) {
        const mappedRows = rows.map(mapping);
        const worksheet = xlsx.utils.json_to_sheet(mappedRows);
        const workbook = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(workbook, worksheet, worksheetName);
        xlsx.writeFile(workbook, addExtension(fileName, format), {
          compression: true,
          ...getOptions(format),
        });
      } else {
        console.error("No rows to export.");
      }
    }
  };

  return (
    <>
      <Button type="primary" onClick={handleClick} disabled={disabled}>
        Export
      </Button>

      <ExportModal
        visible={loading}
        onCancel={cancelExport}
        onComplete={handleComplete}
        isCategory={true}
      />
    </>
  );
};
